// Casa dos relatorios: uma pagina, abas por area.
//
// Este arquivo e o unico lugar que sabe QUAIS areas existem, QUAL tela cada
// sub-aba reaproveita e QUEM pode ver cada uma. A casca (ReportsWrapper.vue) e
// o menu (hubWorlds.js) leem daqui; nenhum dos dois repete a regra.
//
// Regras que o dono aprovou (DECISOES 27):
//   - cinco areas: Atendimento, Vendas, Campanhas, IA, Ligacoes;
//   - sub-aba so aparece para quem tem a permissao E a feature flag que a tela
//     original ja exigia (o `meta` da rota continua sendo a fonte);
//   - area sem nenhuma sub-tela permitida nao aparece;
//   - CSAT, SLA e Times so aparecem para conta que usa (sinal medido, ver
//     useReportsUsage.js): zero uso em producao hoje (relatorios r2, item E:
//     0 respostas de CSAT, 0 politicas de SLA e 1 time em toda a base).
//
// Tudo aqui e funcao pura: nada de store, router ou window. Quem chama injeta
// o que sabe (`canShow`, `usage`).

import { FEATURE_FLAGS } from 'dashboard/featureFlags';

export const REPORTS_HOME_AREAS = Object.freeze([
  {
    id: 'atendimento',
    labelKey: 'REPORT.HOME.AREAS.SERVICE',
    icon: 'i-ph-chat-circle',
    tabs: [
      {
        id: 'visao_geral',
        labelKey: 'REPORT.HOME.TABS.LIVE',
        routeName: 'account_overview_reports',
        permissions: ['administrator', 'report_manage'],
        featureFlag: FEATURE_FLAGS.REPORTS,
        // Tela ao vivo: nao aceita periodo, mostra a explicacao no lugar do
        // seletor em vez de um seletor morto.
        acceptsPeriod: false,
      },
      {
        id: 'conversas',
        labelKey: 'REPORT.HOME.TABS.CONVERSATIONS',
        routeName: 'conversation_reports',
        permissions: ['administrator', 'report_manage'],
        featureFlag: FEATURE_FLAGS.REPORTS,
      },
      {
        id: 'pessoas',
        labelKey: 'REPORT.HOME.TABS.PEOPLE',
        routeName: 'agent_reports_index',
        permissions: ['administrator', 'report_manage'],
        featureFlag: FEATURE_FLAGS.REPORTS,
        childRouteNames: ['agent_reports_show'],
      },
      {
        id: 'caixas',
        labelKey: 'REPORT.HOME.TABS.INBOXES',
        routeName: 'inbox_reports_index',
        permissions: ['administrator', 'report_manage'],
        featureFlag: FEATURE_FLAGS.REPORTS,
        childRouteNames: ['inbox_reports_show'],
      },
      {
        id: 'etiquetas',
        labelKey: 'REPORT.HOME.TABS.LABELS',
        routeName: 'label_reports_index',
        permissions: ['administrator', 'report_manage'],
        featureFlag: FEATURE_FLAGS.REPORTS,
        childRouteNames: ['label_reports_show'],
      },
      {
        id: 'times',
        labelKey: 'REPORT.HOME.TABS.TEAMS',
        routeName: 'team_reports_index',
        permissions: ['administrator', 'report_manage'],
        featureFlag: FEATURE_FLAGS.REPORTS,
        childRouteNames: ['team_reports_show'],
        // SiteUP CRM (relatorios r2, item E): mesma regra de CSAT e SLA. Conta
        // sem nenhum time nao mostra a aba. A rota direta continua abrindo.
        requiresUsage: 'teams',
      },
      {
        id: 'robo',
        labelKey: 'REPORT.HOME.TABS.BOT',
        routeName: 'bot_reports',
        permissions: ['administrator', 'report_manage'],
        featureFlag: FEATURE_FLAGS.REPORTS,
      },
      {
        id: 'csat',
        labelKey: 'REPORT.HOME.TABS.CSAT',
        routeName: 'csat_reports',
        permissions: ['administrator', 'report_manage'],
        featureFlag: FEATURE_FLAGS.REPORTS,
        requiresUsage: 'csat',
      },
      {
        id: 'sla',
        labelKey: 'REPORT.HOME.TABS.SLA',
        routeName: 'sla_reports',
        permissions: ['administrator', 'report_manage'],
        // Decisao 15: SLA exige REPORTS e SLA (lista = todas ligadas).
        featureFlag: [FEATURE_FLAGS.REPORTS, FEATURE_FLAGS.SLA],
        requiresUsage: 'sla',
      },
    ],
  },
  {
    id: 'vendas',
    labelKey: 'REPORT.HOME.AREAS.SALES',
    icon: 'i-ph-currency-circle-dollar',
    tabs: [
      {
        id: 'funil',
        labelKey: 'REPORT.HOME.TABS.FUNNEL',
        routeName: 'kanban_reports',
        permissions: ['administrator', 'report_manage'],
        featureFlag: FEATURE_FLAGS.KANBAN_BOARD,
      },
      {
        id: 'conversoes',
        labelKey: 'REPORT.HOME.TABS.CONVERSIONS',
        routeName: 'integration_events_report',
        permissions: ['administrator', 'agent', 'report_manage'],
      },
    ],
  },
  {
    id: 'campanhas',
    labelKey: 'REPORT.HOME.AREAS.CAMPAIGNS',
    icon: 'i-ph-paper-plane-tilt',
    tabs: [
      {
        id: 'whatsapp',
        labelKey: 'REPORT.HOME.TABS.WHATSAPP_CAMPAIGNS',
        routeName: 'campaign_reports',
        // A tela vivia dentro da configuracao da caixa, que e so de
        // administrador. Fora da configuracao ela mantem o mesmo portao.
        permissions: ['administrator'],
        // Le o periodo unico da casa (a API aceita qualquer intervalo de
        // datas), inclusive o personalizado que o select proprio nao tinha.
      },
    ],
  },
  {
    id: 'ia',
    labelKey: 'REPORT.HOME.AREAS.AI',
    icon: 'i-ph-sparkle',
    tabs: [
      {
        id: 'painel',
        labelKey: 'REPORT.HOME.TABS.AI_PANEL',
        routeName: 'ai_reports',
        // ai_agent_metrics e ai_agent_feedbacks sao admin-only no backend.
        permissions: ['administrator'],
        featureFlag: FEATURE_FLAGS.CAPTAIN_V2,
        // O backend so aceita hoje, 7, 30 e 90 dias: a casa mostra o fixo
        // mais proximo do periodo escolhido e avisa quando nao e o mesmo.
        fixedPeriods: true,
      },
    ],
  },
  {
    id: 'ligacoes',
    labelKey: 'REPORT.HOME.AREAS.CALLS',
    icon: 'i-ph-phone-call',
    tabs: [
      {
        id: 'analitica',
        labelKey: 'REPORT.HOME.TABS.CALL_ANALYTICS',
        routeName: 'voip_reports',
        permissions: ['administrator', 'agent', 'voip_view'],
        featureFlag: FEATURE_FLAGS.VOIP_CALLS,
        // Le o periodo unico da casa e e a unica tela que compara com o
        // periodo anterior: so nela o seletor mostra "Comparar".
        supportsCompare: true,
      },
    ],
  },
]);

// Rotas antigas que continuam existindo e precisam abrir a casa na aba certa.
// As quatro primeiras sao as duplicadas sem link no menu: a rota redireciona
// para a versao `*_overview` (ver reports.routes.js). O mapa existe aqui para
// o deep link antigo ser resolvido mesmo antes do redirect acontecer.
export const LEGACY_ROUTE_REDIRECTS = Object.freeze({
  agent_reports: 'agent_reports_index',
  inbox_reports: 'inbox_reports_index',
  label_reports: 'label_reports_index',
  team_reports: 'team_reports_index',
  voip_performance: 'voip_analytics',
});

const TAB_BY_ROUTE_NAME = (() => {
  const map = new Map();
  REPORTS_HOME_AREAS.forEach(area => {
    area.tabs.forEach(tab => {
      map.set(tab.routeName, { areaId: area.id, tabId: tab.id });
      (tab.childRouteNames || []).forEach(name =>
        map.set(name, { areaId: area.id, tabId: tab.id })
      );
    });
  });
  // A analitica de VOIP tambem e alcancavel pela rota propria do mundo de
  // ligacoes; as duas apontam para a mesma aba.
  map.set('voip_analytics', { areaId: 'ligacoes', tabId: 'analitica' });
  Object.entries(LEGACY_ROUTE_REDIRECTS).forEach(([from, to]) => {
    if (map.has(to)) map.set(from, map.get(to));
  });
  return map;
})();

/** Onde uma rota (nome) cai na casa. `null` quando a rota nao e de relatorio. */
export const resolveReportsLocation = routeName =>
  TAB_BY_ROUTE_NAME.get(routeName) || null;

export const findArea = areaId =>
  REPORTS_HOME_AREAS.find(area => area.id === areaId) || null;

export const findTab = (areaId, tabId) =>
  findArea(areaId)?.tabs.find(tab => tab.id === tabId) || null;

/**
 * Uma sub-aba aparece quando a permissao e a flag da tela original passam E,
 * quando ela depende de uso (CSAT, SLA), quando a conta tem dado.
 *
 * `usage` vem do sinal medido. Enquanto o sinal ainda nao respondeu o valor e
 * `undefined`: nesse caso a aba NAO aparece (nao piscar uma aba que vai sumir),
 * mas a rota direta continua abrindo, porque quem decide isso e o roteador.
 */
export const isTabVisible = (tab, { canShow, usage = {} } = {}) => {
  if (!tab) return false;
  const allowed = canShow
    ? canShow({ featureFlag: tab.featureFlag, permissions: tab.permissions })
    : true;
  if (!allowed) return false;
  if (tab.requiresUsage) return usage[tab.requiresUsage] === true;
  return true;
};

/** Areas com pelo menos uma sub-aba visivel, ja com as sub-abas filtradas. */
export const visibleReportsAreas = (context = {}) =>
  REPORTS_HOME_AREAS.map(area => ({
    ...area,
    tabs: area.tabs.filter(tab => isTabVisible(tab, context)),
  })).filter(area => area.tabs.length > 0);

/** A casa inteira some quando a pessoa nao pode ver nenhuma area. */
export const canSeeReportsHome = (context = {}) =>
  visibleReportsAreas(context).length > 0;

/**
 * Para onde mandar quem abriu a casa sem dizer a aba (ou pediu uma aba que
 * nao pode ver). Devolve o nome de rota da primeira sub-aba visivel, ou null.
 */
export const defaultReportsRouteName = (context = {}, preferredAreaId = '') => {
  const areas = visibleReportsAreas(context);
  if (!areas.length) return null;
  const area = areas.find(item => item.id === preferredAreaId) || areas[0];
  return area.tabs[0].routeName;
};

/** Nome de rota da area, mantendo a sub-aba quando ela existir e for visivel. */
export const routeNameForArea = (areaId, context = {}) => {
  const area = visibleReportsAreas(context).find(item => item.id === areaId);
  return area ? area.tabs[0].routeName : null;
};
